import {
  evalMapping,
  parseJqToBuilderState,
} from 'components/routes/jq-filter-builder/helpers';

// What an outbound endpoint would send for a filter, computed with the builder's
// evaluator: there is no jq engine in the browser, so filters the builder can't
// represent have no preview.
export type OutboundPreview =
  | { kind: 'empty' }
  | { kind: 'custom' }
  // Builder-shaped, but for the other batch setting: it would run on the wrong input shape.
  | { kind: 'batch_mismatch'; writtenForBatch: boolean }
  | { kind: 'no_request'; failedConditions: string[] }
  // A path jq can't index (e.g. a string), or a transform jq would fail on (NaN, e.g. tonumber on text).
  | { kind: 'error'; fields: string[] }
  | { kind: 'request'; body: any; isArray: boolean; bytes: number };

function utf8Length(text: string): number {
  let bytes = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0)!;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

function failingFields(value: any, path = ''): string[] {
  if (typeof value === 'number' && isNaN(value)) return [path || '.'];
  if (Array.isArray(value))
    return value.flatMap((item, i) => failingFields(item, `${path}[${i}]`));
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) =>
      failingFields(item, `${path}.${key}`),
    );
  }
  return [];
}

export function previewOutbound(
  jq: string | undefined | null,
  input: any,
  batch: boolean,
): OutboundPreview {
  const parsed = parseJqToBuilderState(jq ?? '', { mode: 'outbound', batch });
  if (!parsed) return { kind: 'empty' };
  if (!parsed.isFullyParseable) {
    const other = parseJqToBuilderState(jq ?? '', {
      mode: 'outbound',
      batch: !batch,
    });
    return other?.isFullyParseable
      ? { kind: 'batch_mismatch', writtenForBatch: !batch }
      : { kind: 'custom' };
  }
  const result = evalMapping(
    input,
    {},
    parsed.addl,
    parsed.conds,
    'obv',
    {},
    {},
    { mode: 'outbound', batch },
  );
  if (result.errorPaths?.length)
    return { kind: 'error', fields: result.errorPaths };
  if (result.data === null || result.data === undefined)
    return { kind: 'no_request', failedConditions: result.failedConditions };
  const fields = failingFields(result.data);
  if (fields.length) return { kind: 'error', fields };
  return {
    kind: 'request',
    body: result.data,
    isArray: Array.isArray(result.data),
    bytes: utf8Length(JSON.stringify(result.data)),
  };
}

/** The jq input for a preview: one record, or in batch mode an array of records. */
export function previewInput(records: any[], batch: boolean): any {
  if (!batch) return records[0];
  return records.length === 1 ? [records[0], records[0]] : records;
}
